import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DashboardWidgetCardComponent } from '../dashboard-shared/dashboard-widget-card/dashboard-widget-card.component';
import { DashboardApiService } from '../services/dashboard-api.service';
import { catchError, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../../auth-pages/services/auth.service';
import { Router } from '@angular/router';
import {
  MonthlyRevenue,
  SalesFunnel,
  MonthlyLeads,
  MyAgenda,
  TicketsByStatus,
  BusinessesByStatus,
  EmailFunnel,
  MeetingsPivot,
  WidgetState
} from '../models/dashboard.model';
import { QuoteMetricsResponse } from '../../quotes/models/quote.model';

  interface WidgetData<T> {
    state: WidgetState;
    data?: T;
  }

@Component({
  selector: 'app-dashboard-page',
  imports: [
    CommonModule,
    DashboardWidgetCardComponent
  ],
  templateUrl: './dashboard-page.component.html',
  styles: ``,
})
export class DashboardPageComponent {
  private dashboardApi = inject(DashboardApiService);
  private router = inject(Router);

  // 
  private auth = inject(AuthService);
  // Tab activo del widget de Reuniones
  meetingsActiveTab: 'types' | 'contacts' | 'companies' = 'types';

  private get businessId(): number {
    return this.auth.getBusinessId() ?? 0;
  }

  private get userPropietyId(): number {
    return this.auth.getCurrentUser()?.id ?? 0;
  }
  
  // Fecha actual para el header (formato simple, luego usamos pipe date)
  todayLabel = new Date().toLocaleDateString('es-PE', {
    day: '2-digit', month: 'long', year: 'numeric'
  });

  // Estado de cada widget
  revenue: WidgetData<MonthlyRevenue> = { state: 'loading' };
  funnel: WidgetData<SalesFunnel> = { state: 'loading' };
  leads: WidgetData<MonthlyLeads> = { state: 'loading' };
  agenda: WidgetData<MyAgenda> = { state: 'loading' };
  tickets: WidgetData<TicketsByStatus> = { state: 'loading' };
  businesses: WidgetData<BusinessesByStatus> = { state: 'loading' };
  emailFunnel: WidgetData<EmailFunnel> = { state: 'loading' };
  meetings: WidgetData<MeetingsPivot> = { state: 'loading' };
  quotes: WidgetData<QuoteMetricsResponse> = { state: 'loading' };
  // El endpoint de métricas de cotizaciones requiere rol Manager+. Si responde 403,
  // ocultamos el widget por completo en vez de mostrar un error.
  quotesForbidden = false;

  ngOnInit(): void {
    this.loadRevenue();
    this.loadFunnel();
    this.loadLeads();
    this.loadAgenda();
    this.loadTickets();
    this.loadBusinesses();
    this.loadEmailFunnel();
    this.loadMeetings();
    this.loadQuotes();
  }

  loadRevenue(): void {
    this.revenue = { state: 'loading' };
    this.dashboardApi.getMonthlyRevenue({ businessId: this.businessId })
      .pipe(catchError(() => { this.revenue = { state: 'error' }; return of(null); }))
      .subscribe(data => {
        if (data) this.revenue = { state: 'ready', data };
      });
  }

  loadFunnel(): void {
    this.funnel = { state: 'loading' };
    this.dashboardApi.getSalesFunnel({ businessId: this.businessId })
      .pipe(catchError(() => { this.funnel = { state: 'error' }; return of(null); }))
      .subscribe(data => {
        if (!data) return;
        this.funnel = {
          state: data.stages.length === 0 ? 'empty' : 'ready',
          data
        };
      });
  }

  loadLeads(): void {
    this.leads = { state: 'loading' };
    this.dashboardApi.getMonthlyLeads({ businessId: this.businessId })
      .pipe(catchError(() => { this.leads = { state: 'error' }; return of(null); }))
      .subscribe(data => {
        if (!data) return;
        // Mientras el backend no soporte filtro de fecha, totalLeads=0
        // se interpreta como "pausado" y mostramos un mensaje específico.
        this.leads = {
          state: data.totalLeads === 0 ? 'empty' : 'ready',
          data
        };
      });
  }

  loadAgenda(): void {
    this.agenda = { state: 'loading' };
    this.dashboardApi.getMyAgenda({
      businessId: this.businessId,
      userPropietyId: this.userPropietyId
    })
      .pipe(catchError(() => { this.agenda = { state: 'error' }; return of(null); }))
      .subscribe(data => {
        if (!data) return;
        const isEmpty = data.callsToday.length === 0 && data.overdueTasks.length === 0;
        this.agenda = { state: isEmpty ? 'empty' : 'ready', data };
      });
  }

  // ============================================================
  // Carga de widgets nuevos
  // ============================================================

  loadTickets(): void {
    this.tickets = { state: 'loading' };
    this.dashboardApi.getTicketsByStatus({ businessId: this.businessId })
      .pipe(catchError(() => { this.tickets = { state: 'error' }; return of(null); }))
      .subscribe(data => {
        if (!data) return;
        this.tickets = {
          state: data.total === 0 ? 'empty' : 'ready',
          data
        };
      });
  }

  loadBusinesses(): void {
    this.businesses = { state: 'loading' };
    this.dashboardApi.getBusinessesByStatus({ businessId: this.businessId })
      .pipe(catchError(() => { this.businesses = { state: 'error' }; return of(null); }))
      .subscribe(data => {
        if (!data) return;
        this.businesses = {
          state: data.total === 0 ? 'empty' : 'ready',
          data
        };
      });
  }

  loadEmailFunnel(): void {
    this.emailFunnel = { state: 'loading' };
    this.dashboardApi.getEmailFunnel({ businessId: this.businessId })
      .pipe(catchError(() => { this.emailFunnel = { state: 'error' }; return of(null); }))
      .subscribe(data => {
        if (!data) return;
        const isEmpty = data.campaignsByStatus.total === 0;
        this.emailFunnel = { state: isEmpty ? 'empty' : 'ready', data };
      });
  }

  loadMeetings(): void {
    this.meetings = { state: 'loading' };
    this.dashboardApi.getMeetingsPivot({ businessId: this.businessId })
      .pipe(catchError(() => { this.meetings = { state: 'error' }; return of(null); }))
      .subscribe(data => {
        if (!data) return;
        this.meetings = {
          state: data.totals.grandTotal === 0 ? 'empty' : 'ready',
          data
        };
      });
  }

  loadQuotes(): void {
    this.quotesForbidden = false;
    this.quotes = { state: 'loading' };
    this.dashboardApi.getQuoteMetrics({ businessId: this.businessId })
      .pipe(catchError((err: HttpErrorResponse) => {
        // 403 = el usuario no es Manager+: ocultamos el widget sin romper el dashboard.
        if (err?.status === 403) {
          this.quotesForbidden = true;
        } else {
          this.quotes = { state: 'error' };
        }
        return of(null);
      }))
      .subscribe(data => {
        if (!data) return;
        this.quotes = {
          state: data.totalQuotes === 0 ? 'empty' : 'ready',
          data
        };
      });
  }

  // Helpers para el widget 1
  get isRevenuePositive(): boolean {
    return (this.revenue.data?.variationPercent ?? 0) >= 0;
  }

  get revenueVariationAbs(): number {
    return Math.abs(this.revenue.data?.variationPercent ?? 0);
  }

  // Helpers para el widget 2
  get maxLeadsBySource(): number {
    const sources = this.leads.data?.bySource ?? [];
    if (sources.length === 0) return 0;
    return Math.max(...sources.map(s => s.count));
  }

  totalContactsAcrossSources(bySource: { count: number }[] | undefined): number {
    return bySource?.reduce((sum, s) => sum + s.count, 0) ?? 0;
  }
  
  getSourceWidthPercent(count: number): number {
    if (this.maxLeadsBySource === 0) return 0;
    return Math.max((count / this.maxLeadsBySource) * 100, 8);
  }

  getSourcePercentage(count: number): number {
    const total = this.leads.data?.totalLeads ?? 0;
    if (total === 0) return 0;
    return (count / total) * 100;
  }

  // Helpers para el widget 3
  get maxStageAmount(): number {
    const stages = this.funnel.data?.stages ?? [];
    if (stages.length === 0) return 0;
    return Math.max(...stages.map(s => s.totalAmount));
  }

  getStageWidthPercent(amount: number): number {
    if (this.maxStageAmount === 0) return 0;
    return Math.max((amount / this.maxStageAmount) * 100, 8); // mínimo 8% para que se vea la barra
  }

  // Paleta de morados de menos a más oscuro (refuerza visualmente el embudo)
  private readonly funnelColors = [
    'bg-purple-200 dark:bg-purple-900/40',
    'bg-purple-300 dark:bg-purple-800/50',
    'bg-purple-400 dark:bg-purple-700/60',
    'bg-purple-500 dark:bg-purple-600/70',
    'bg-purple-700 dark:bg-purple-500/80',
    'bg-purple-800 dark:bg-purple-400/90',
    'bg-purple-900 dark:bg-purple-300'
  ];

  getStageColor(index: number, total: number): string {
    // Distribuye los colores disponibles según cuántas etapas haya
    const colorIndex = Math.floor((index / Math.max(total - 1, 1)) * (this.funnelColors.length - 1));
    return this.funnelColors[colorIndex] ?? this.funnelColors[0];
  }


  // Helpers para el widget 4
  formatTaskTime(isoDatetime: string): string {
    const date = new Date(isoDatetime);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  // Días de atraso de una tarea respecto a hoy
  getDaysOverdue(isoDatetime: string): number {
    const dueDate = new Date(isoDatetime);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    const diffMs = today.getTime() - dueDate.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }

  // Etiqueta corta del retraso: "-1d", "-2d", etc.
  getOverdueLabel(isoDatetime: string): string {
    const days = this.getDaysOverdue(isoDatetime);
    return `-${days}d`;
  }

  // Helpers — Widget Tickets (barras apiladas)
  // ============================================================
  /** Devuelve el ancho mínimo del segmento para que se vea (al menos 5%). */
  getTicketSegmentWidth(percent: number): number {
    if (percent === 0) return 0;
    return Math.max(percent, 5);
  }

  /** Formatea minutos en formato corto: "12 min" o "3.5 h". */
  formatMinutes(min: number | null): string {
    if (min == null) return '—';
    if (min < 60) return `${Math.round(min)} min`;
    const hours = (min / 60).toFixed(1);
    return `${hours} h`;
  }

  // ============================================================
  // Helpers — Widget Negocios (dona SVG)
  // ============================================================
  /**
   * Calcula los strokeDasharray y strokeDashoffset para cada segmento de la dona.
   * Asume un círculo con r=15.915 (circunferencia = 100, fácil de calcular).
   */
  getDonutSegments(): { color: string; dashArray: string; dashOffset: number; label: string; percent: number }[] {
    const slices = (this.businesses.data?.slices ?? []).filter(s => s.count > 0);
    let offset = 0;
    return slices.map(s => {
      const seg = {
        color: s.color,
        dashArray: `${s.percent} ${100 - s.percent}`,
        dashOffset: -offset,
        label: s.stageName,
        percent: s.percent
      };
      offset += s.percent;
      return seg;
    });
  }

  // ============================================================
  // Helpers — Widget Correo (funnel horizontal)
  // ============================================================
  getEmailStageWidth(percentFromTotal: number): number {
    return Math.max(percentFromTotal, 10); // mínimo 10% para que se vea
  }

  setMeetingsTab(tab: 'types' | 'contacts' | 'companies'): void {
    this.meetingsActiveTab = tab;
  }

  // ============================================================
  // Drill-down handlers de los widgets nuevos
  // ============================================================
  onTicketsDrillDown(): void {
    this.router.navigate(['/tickets']);
  }

  onBusinessesDrillDown(): void {
    this.router.navigate(['/deals/lista']);
  }

  onMeetingsDrillDown(): void {
    this.router.navigate(['/meeting']);
  }

  /** Drill-down: ir al listado de cotizaciones. */
  onQuotesDrillDown(): void {
    this.router.navigate(['/cotizaciones']);
  }

  /** Drill-down: ir al listado de contactos. */
  onLeadsDrillDown(): void {
    this.router.navigate(['/contactos']);
  }

  /** Drill-down: ir al listado de tareas. */
  onAgendaDrillDown(): void {
    this.router.navigate(['/tareas']);
  }

  /** Drill-down: ir al listado de campañas de marketing. */
  onEmailFunnelDrillDown(): void {
    this.router.navigate(['/marketing-email/estado']);
  }


  // Navegar a detalle de tarea al hacer click en la agenda
  onAgendaItemClick(taskId: number): void {
    this.router.navigate(['/tareas/editar', taskId]);
  }

  // Drill-down handlers
  onRevenueDrillDown(): void {
    this.router.navigate(['/deals/lista'], {
      queryParams: {
        activeOnly: false,
        showFilters: true
      }
    });
  }

  onStageClick(stageId: number): void {
    this.router.navigate(['/deals/lista'], {
      queryParams: {
        dealCycle: stageId,
        showFilters: true
      }
    });
  }

  onFunnelDrillDown(): void {
    this.router.navigate(['/deals/lista'], {
      queryParams: {
        activeOnly: true
      }
    });
  }

  /** Navega al editar de un contacto desde el widget de Reuniones. */
  onMeetingContactClick(contactId: number): void {
    this.router.navigate(['/contactos/editar', contactId]);
  }

  /** Navega al editar de una empresa desde el widget de Reuniones. */
  onMeetingCompanyClick(companyId: number): void {
    this.router.navigate(['/empresas/editar', companyId]);
  }
}
